import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary } from "@/pages/Senders";
import { StatusBadge } from "@/components/StatusBadge";
import { estimateCampaignPlanTime } from "@/lib/campaignEta";
import { PaperPlaneTilt } from "@phosphor-icons/react";

const STEPS = [
  "Details",
  "Senders",
  "Emails per mailbox",
  "Batch & pauses",
  "Sequential mapping",
  "Review",
];

export default function CampaignBuilder({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "",
    description: "",
    recipient_list_id: "",
    content_mode: "manual",
    content_template_id: "",
    inline_subject: "Quick question about [companyName]",
    inline_text_body: "Hi [firstName],\n\nQuick note.\n\nThanks\n[senderFullName]",
    inline_html_body: "",
    ai_settings: { email_type: "cold outreach", tone: "professional", length: "short (100-150 words)", language: "English", instructions: "" },
    loan_amount_dollar_sign: false,
    loan_amount_dollar_percent: 100,
    cta_link_enabled: true,
    sender_provider_filter: "all",
    sender_ids: [],
    emails_per_mailbox: 10,
    batch_size: 250,
    pause_minimum_seconds: 1800,
    pause_maximum_seconds: 3600,
    worker_count: 10,
    timezone: "UTC",
  });

  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [senders, setSenders] = useState([]);
  const [review, setReview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  useEffect(() => {
    const fetchAllSenders = async () => {
      const pageSize = 2000;
      let skip = 0;
      let all = [];
      // Loop until we've fetched every sender (handles users with >2000 mailboxes)
      // Safety cap at 50 pages (=100k mailboxes) to avoid runaway loops.
      for (let i = 0; i < 50; i++) {
        const r = await api.get("/senders", { params: { limit: pageSize, skip } });
        const batch = r.data.senders || [];
        all = all.concat(batch);
        const total = r.data.total ?? all.length;
        skip += batch.length;
        if (batch.length < pageSize || all.length >= total) break;
      }
      return all;
    };

    Promise.all([
      api.get("/recipients/lists"),
      api.get("/content/templates"),
      fetchAllSenders(),
    ]).then(([a, b, allSenders]) => {
      setLists(a.data.lists);
      setTemplates(b.data.templates);
      setSenders(allSenders);
    });
  }, []);

  const canNext = () => {
    if (step === 0) return form.name && form.recipient_list_id;
    if (step === 1) return true;
    return true;
  };

  const doReview = async () => {
    // Create draft first if not already
    let id = createdId;
    if (!id) {
      try {
        setSaving(true);
        const r = await api.post("/campaigns", form);
        id = r.data.id;
        setCreatedId(id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Create failed");
        setSaving(false);
        return;
      } finally { setSaving(false); }
    }
    try {
      const r = await api.post(`/campaigns/${id}/review`);
      setReview(r.data);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    }
  };

  const startCampaign = async () => {
    setSaving(true);
    try {
      await api.post(`/campaigns/${createdId}/start`);
      toast.success("Campaign started");
      onClose();
      onCreated?.(createdId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    } finally { setSaving(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await api.post("/campaigns", form);
      toast.success("Draft saved");
      onClose();
      onCreated?.(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="campaign-builder">
      <div className="w-full max-w-4xl bg-[#0A0A0A] border border-[#2A2A2A] max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-[#2A2A2A] flex items-start justify-between">
          <div>
            <div className="micro mb-1">Campaign Builder · Step {step + 1} of {STEPS.length}</div>
            <h3 className="font-heading text-2xl font-semibold">{STEPS[step]}</h3>
          </div>
          <BtnSecondary onClick={onClose} testid="builder-close">Close</BtnSecondary>
        </div>

        <div className="flex border-b border-[#2A2A2A]">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 py-2 px-3 text-center font-mono text-[10px] uppercase tracking-wider border-r border-[#2A2A2A] last:border-r-0 ${i === step ? "bg-[#E1FF00] text-black" : i < step ? "text-[#00FF66]" : "text-[#71717A]"}`}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 0 && (
            <div className="space-y-4 max-w-2xl">
              <Field label="Campaign name">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="cb-name"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
              </Field>
              <Field label="Description">
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="cb-desc"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
              </Field>
              <Field label="Recipient list">
                <select value={form.recipient_list_id} onChange={(e) => setForm({ ...form, recipient_list_id: e.target.value })}
                  data-testid="cb-recipient-list"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white">
                  <option value="">— select —</option>
                  {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.valid_rows} recipients)</option>)}
                </select>
              </Field>
              <Field label="Content mode">
                <select value={form.content_mode} onChange={(e) => setForm({ ...form, content_mode: e.target.value })}
                  data-testid="cb-content-mode"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white">
                  <option value="manual">Manual</option>
                  <option value="spintax">Spintax</option>
                  <option value="ai_realtime">AI real-time (DeepSeek)</option>
                  <option value="ai_pooled">AI pooled (pre-generated · faster)</option>
                </select>
              </Field>
              <Field label="Template (optional)">
                <select value={form.content_template_id} onChange={(e) => setForm({ ...form, content_template_id: e.target.value })}
                  data-testid="cb-template"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white">
                  <option value="">— use inline below —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.content_type}</option>)}
                </select>
              </Field>
              <Field label="Subject (inline)">
                <input value={form.inline_subject} onChange={(e) => setForm({ ...form, inline_subject: e.target.value })}
                  data-testid="cb-subject"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
              </Field>
              <Field label="Body (inline)">
                <textarea rows={8} value={form.inline_text_body} onChange={(e) => setForm({ ...form, inline_text_body: e.target.value })}
                  data-testid="cb-body"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
              </Field>

              {form.content_mode === "ai_pooled" && (
                <div className="border border-[#E1FF00]/30 bg-[#E1FF00]/5 p-3 text-[11px] font-mono text-[#A1A1AA] leading-relaxed" data-testid="cb-pooled-hint">
                  <span className="text-[#E1FF00]">Pooled mode:</span> the campaign will consume pre-generated
                  content from the <a className="underline text-[#00E5FF]" href="/content-pool">Content Pool</a>.
                  Make sure the pool has enough items (target ≥ your recipient count) before starting.
                  If the pool runs empty during a send, that recipient row fails with error
                  &quot;no pooled content available&quot; — it will NOT auto-fallback to real-time.
                </div>
              )}

              {form.content_mode === "ai_realtime" && (
                <div className="border border-[#00E5FF]/30 bg-[#0A0A0A] p-3 space-y-3">
                  <div className="text-[10px] text-[#00E5FF] font-mono uppercase tracking-wider">
                    AI Studio length constraints
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      ["subject_min_chars", "Subj min (chars)", 40],
                      ["subject_max_chars", "Subj max (chars)", 60],
                      ["body_min_paragraphs", "Body min (para)", 4],
                      ["body_max_paragraphs", "Body max (para)", 6],
                    ].map(([k, label, dflt]) => (
                      <label key={k} className="block">
                        <span className="micro block mb-1 text-[#71717A]">{label}</span>
                        <input type="number" min={1}
                          value={form.ai_settings?.[k] ?? dflt}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              ai_settings: {
                                ...(form.ai_settings || {}),
                                [k]: Number(e.target.value) || dflt,
                              },
                            })
                          }
                          data-testid={`cb-ai-${k}`}
                          className="w-full bg-black border border-[#2A2A2A] px-2 py-1.5 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
                      </label>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#71717A] leading-snug">
                    Subject placeholders rotate every 10 recipients (by original CSV row) — 40% <span className="font-mono text-[#E1FF00]">[loanAmount]+[firstName]</span>, 40% <span className="font-mono text-[#E1FF00]">[firstName]</span>, 10% <span className="font-mono text-[#E1FF00]">[phone]</span>, 10% plain:{" "}
                    <span className="font-mono text-[#E1FF00]">[loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span> ·
                    <span className="font-mono text-[#E1FF00]"> [loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span> · plain ·
                    <span className="font-mono text-[#E1FF00]"> [loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span> · <span className="font-mono text-[#E1FF00]">[phone]</span> ·
                    <span className="font-mono text-[#E1FF00]"> [loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span>
                  </div>
                  <label className="block">
                    <span className="micro block mb-1 text-[#71717A]">AI instructions (optional)</span>
                    <textarea rows={2}
                      value={form.ai_settings?.instructions || ""}
                      onChange={(e) => setForm({ ...form, ai_settings: { ...(form.ai_settings || {}), instructions: e.target.value } })}
                      data-testid="cb-ai-instructions"
                      placeholder="Extra guidance for the model."
                      className="w-full bg-black border border-[#2A2A2A] px-2 py-1.5 text-xs text-white focus:border-[#E1FF00] focus:outline-none" />
                  </label>
                </div>
              )}

              {/* [loanAmount] "$" prefix — applies to every content mode */}
              <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-3 space-y-3 mt-4" data-testid="cb-loan-dollar-box">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="cb-loan-dollar-toggle">
                  <input type="checkbox" checked={!!form.loan_amount_dollar_sign}
                    onChange={(e) => setForm({ ...form, loan_amount_dollar_sign: e.target.checked })}
                    className="accent-[#E1FF00] w-4 h-4" />
                  <span className="text-xs font-mono text-white">
                    Add <span className="text-[#E1FF00]">$</span> in front of <span className="text-[#E1FF00]">[loanAmount]</span>
                  </span>
                </label>
                {form.loan_amount_dollar_sign && (
                  <div className="pl-6 space-y-1">
                    <span className="micro block text-[#71717A]">
                      Apply to what % of emails? (100 = every email gets "$"; lower = random split)
                    </span>
                    <div className="flex items-center gap-2">
                      <input type="number" min={1} max={100} value={form.loan_amount_dollar_percent}
                        onChange={(e) => setForm({ ...form, loan_amount_dollar_percent: Math.max(1, Math.min(100, Number(e.target.value) || 0)) })}
                        data-testid="cb-loan-dollar-percent"
                        className="w-24 bg-black border border-[#2A2A2A] px-3 py-1.5 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
                      <span className="text-xs font-mono text-[#71717A]">
                        % get <span className="text-[#E1FF00]">$[loanAmount]</span>, the rest stay plain
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA link toggle — off = send normal email with no hyperlink / no [ctaLink] */}
              <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-3 space-y-2 mt-4" data-testid="cb-cta-link-box">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono text-white">
                      CTA link <span className={form.cta_link_enabled ? "text-[#E1FF00]" : "text-[#FF9500]"}>{form.cta_link_enabled ? "ON" : "OFF"}</span>
                    </div>
                    <div className="micro text-[#71717A] mt-0.5">
                      {form.cta_link_enabled
                        ? "Emails include the clickable CTA link."
                        : "No hyperlink and no [ctaLink] — the CTA words stay as plain text (link-free test/warmup sends)."}
                    </div>
                  </div>
                  <button type="button"
                    onClick={() => setForm({ ...form, cta_link_enabled: !form.cta_link_enabled })}
                    data-testid="cb-cta-link-toggle"
                    aria-pressed={!!form.cta_link_enabled}
                    className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${form.cta_link_enabled ? "bg-[#E1FF00]" : "bg-[#2A2A2A]"}`}
                    title="Toggle CTA hyperlink">
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-black transition-transform ${form.cta_link_enabled ? "translate-x-7" : ""}`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="flex gap-2">
                {[["all", "All"], ["google", "Google only"], ["microsoft", "Microsoft only"]].map(([v, l]) => (
                  <button key={v} onClick={() => setForm({ ...form, sender_provider_filter: v, sender_ids: [] })}
                    data-testid={`cb-provider-${v}`}
                    className={`px-4 py-1.5 text-xs uppercase font-mono tracking-wider ${form.sender_provider_filter === v ? "bg-[#E1FF00] text-black" : "border border-[#2A2A2A] text-[#A1A1AA]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="micro">Or select specific senders (leave empty to use all active in the provider filter)</div>
              <SenderPicker
                senders={senders}
                providerFilter={form.sender_provider_filter}
                selectedIds={form.sender_ids}
                onChange={(ids) => setForm({ ...form, sender_ids: ids })}
              />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 max-w-md">
              <div className="micro">How many emails should each mailbox send in this campaign?</div>
              <div className="flex flex-wrap gap-2">
                {[1, 5, 10, 20].map((n) => (
                  <button key={n} onClick={() => setForm({ ...form, emails_per_mailbox: n })}
                    data-testid={`cb-epm-${n}`}
                    className={`px-4 py-2 text-sm font-mono ${form.emails_per_mailbox === n ? "bg-[#E1FF00] text-black" : "border border-[#2A2A2A] text-[#A1A1AA]"}`}>
                    {n}
                  </button>
                ))}
                <input type="number" min={1} value={form.emails_per_mailbox}
                  onChange={(e) => setForm({ ...form, emails_per_mailbox: Number(e.target.value) })}
                  data-testid="cb-epm-custom"
                  className="w-24 bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 max-w-md">
              <Field label="Batch size (rows processed before pause)">
                <input type="number" value={form.batch_size}
                  onChange={(e) => setForm({ ...form, batch_size: Number(e.target.value) })}
                  data-testid="cb-batch-size"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white" />
              </Field>
              <Field label="Pause minimum (seconds)">
                <input type="number" value={form.pause_minimum_seconds}
                  onChange={(e) => setForm({ ...form, pause_minimum_seconds: Number(e.target.value) })}
                  data-testid="cb-pause-min"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white" />
              </Field>
              <Field label="Pause maximum (seconds)">
                <input type="number" value={form.pause_maximum_seconds}
                  onChange={(e) => setForm({ ...form, pause_maximum_seconds: Number(e.target.value) })}
                  data-testid="cb-pause-max"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white" />
              </Field>
              <Field label="Concurrent workers (1–100)">
                <input type="number" min={1} max={100} value={form.worker_count}
                  onChange={(e) => setForm({ ...form, worker_count: Number(e.target.value) })}
                  data-testid="cb-workers"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white" />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3 max-w-2xl">
              <div className="micro">Sequential one-to-one mapping</div>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                Sender 1 through Proxy 1 → Recipient 1<br />
                Sender 2 through Proxy 2 → Recipient 2<br />
                Sender 3 through Proxy 3 → Recipient 3
              </p>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                After the last eligible sender, we cycle back to Sender 1 and continue with the next recipient.
                Every send uses only its assigned static proxy. If the proxy fails, the send fails — no direct fallback.
              </p>
              <div className="border border-[#E1FF00]/30 bg-[#E1FF00]/5 p-4 mt-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#E1FF00] mb-2">Guarantees</div>
                <ul className="text-xs space-y-1 text-[#E4E4E7] font-mono">
                  <li>· Recipient order preserved</li>
                  <li>· Sender order preserved (upload order)</li>
                  <li>· One mailbox → one recipient per send</li>
                  <li>· Strict proxy-only sending</li>
                  <li>· Deterministic pre-assignment</li>
                </ul>
              </div>
            </div>
          )}

          {step === 5 && review && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <ReviewRow label="Campaign name" v={review.campaign_name} />
                <ReviewRow label="Recipients" v={review.recipient_count} />
                <ReviewRow label="Total senders" v={review.total_senders} />
                <ReviewRow label="Eligible (valid proxy)" v={review.senders_with_valid_proxy} tone="success" />
                <ReviewRow label="Failed proxy" v={review.senders_with_failed_proxy} tone="error" />
                <ReviewRow label="Missing proxy" v={review.senders_missing_proxy} tone="warning" />
                <ReviewRow label="Emails per mailbox" v={review.emails_per_mailbox} />
                <ReviewRow label="Campaign capacity" v={review.campaign_capacity} tone="brand" />
                <ReviewRow label="Batch size" v={review.batch_size} />
                <ReviewRow label="Pause range" v={`${review.pause_minimum_seconds}s – ${review.pause_maximum_seconds}s`} />
                <ReviewRow label="Workers" v={review.worker_count} />
                <ReviewRow label="Estimated batches" v={review.estimated_batches} />
                <ReviewRow
                  label="Estimated duration"
                  v={estimateCampaignPlanTime({
                    recipient_count: Math.min(review.recipient_count, review.campaign_capacity),
                    batch_size: review.batch_size,
                    pause_minimum_seconds: review.pause_minimum_seconds,
                    pause_maximum_seconds: review.pause_maximum_seconds,
                    worker_count: review.worker_count,
                  }).human}
                  tone="brand"
                />
                <ReviewRow label="Content mode" v={review.content_mode} />
              </div>
              <div className="text-[11px] text-[#71717A] font-mono leading-snug">
                Estimated duration assumes ~5s per email through proxy divided by {review.worker_count} concurrent workers,
                plus the average pause of {(Number(review.pause_minimum_seconds) + Number(review.pause_maximum_seconds)) / 2}s
                between {review.estimated_batches} batches. Live ETA on the dashboard refines this using measured throughput once sends begin.
              </div>
              {review.senders_with_valid_proxy === 0 && (
                <div className="text-xs font-mono text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3">
                  No eligible senders with valid proxy. Test proxies before starting.
                </div>
              )}

              <TestSendPanel
                form={form}
                senders={senders}
              />
            </div>
          )}
        </div>

        <div className="p-5 border-t border-[#2A2A2A] flex justify-between">
          <div>
            {step > 0 && <BtnSecondary onClick={() => setStep(step - 1)} testid="cb-back">← Back</BtnSecondary>}
          </div>
          <div className="flex gap-2">
            {step < 4 && <BtnSecondary onClick={() => setStep(step + 1)} testid="cb-next" disabled={!canNext()}>Next →</BtnSecondary>}
            {step === 4 && <BtnPrimary onClick={doReview} testid="cb-review" disabled={saving}>Review</BtnPrimary>}
            {step === 5 && (
              <>
                <BtnSecondary onClick={saveDraft} testid="cb-save-draft" disabled={saving}>Save as draft</BtnSecondary>
                <BtnPrimary onClick={startCampaign} testid="cb-start" disabled={saving || (review?.senders_with_valid_proxy === 0)}>
                  Start Campaign
                </BtnPrimary>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="micro block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ReviewRow({ label, v, tone }) {
  const c = tone === "success" ? "text-[#00FF66]" :
            tone === "error" ? "text-[#FF3B30]" :
            tone === "warning" ? "text-[#FF9500]" :
            tone === "brand" ? "text-[#E1FF00]" : "text-white";
  return (
    <div className="border border-[#2A2A2A] p-3">
      <div className="micro mb-1">{label}</div>
      <div className={`data-num text-xl font-semibold ${c}`}>{v}</div>
    </div>
  );
}

// ---- Sender picker ----------------------------------------------------
// Search, select-all-filtered, and comma-separated bulk-select of mailboxes.
// Shows a live "N of M selected" counter so the user always knows the scope.
function SenderPicker({ senders, providerFilter, selectedIds, onChange }) {
  const [search, setSearch] = useState("");
  const [bulk, setBulk] = useState("");
  const filtered = senders.filter((s) => {
    if (providerFilter !== "all" && s.provider !== providerFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.email || "").toLowerCase().includes(q)
        || (s.first_name || "").toLowerCase().includes(q)
        || (s.last_name || "").toLowerCase().includes(q);
  });
  const selectedSet = new Set(selectedIds);
  const filteredSelectedCount = filtered.filter((s) => selectedSet.has(s.id)).length;
  const allFilteredSelected = filtered.length > 0 && filteredSelectedCount === filtered.length;

  const toggleAllFiltered = () => {
    const next = new Set(selectedIds);
    if (allFilteredSelected) filtered.forEach((s) => next.delete(s.id));
    else filtered.forEach((s) => next.add(s.id));
    onChange(Array.from(next));
  };

  const bulkPaste = () => {
    if (!bulk.trim()) return;
    // Accept comma-, space-, semicolon-, or newline-separated emails.
    const tokens = bulk.split(/[,\s;]+/).map((t) => t.trim().toLowerCase()).filter(Boolean);
    const byEmail = new Map(senders.map((s) => [(s.email || "").toLowerCase(), s.id]));
    const next = new Set(selectedIds);
    let matched = 0, missing = 0;
    tokens.forEach((t) => {
      const id = byEmail.get(t);
      if (id) { next.add(id); matched += 1; }
      else missing += 1;
    });
    onChange(Array.from(next));
    setBulk("");
    // Light feedback via title attribute; toast would need a top-level import here.
    const el = document.querySelector('[data-testid="sp-bulk-result"]');
    if (el) el.textContent = `matched ${matched}${missing ? ` · ${missing} not found` : ""}`;
  };

  return (
    <div className="space-y-2" data-testid="sender-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="search email or name..."
          data-testid="sp-search"
          className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-1.5 text-xs text-white flex-1 min-w-[200px] focus:border-[#E1FF00] focus:outline-none font-mono"
        />
        <button
          onClick={toggleAllFiltered}
          data-testid="sp-select-all"
          className="px-3 py-1.5 text-[11px] uppercase font-mono tracking-wider border border-[#E1FF00]/40 text-[#E1FF00] hover:bg-[#E1FF00]/10"
        >
          {allFilteredSelected ? "Unselect filtered" : `Select all filtered (${filtered.length})`}
        </button>
        <button
          onClick={() => onChange([])}
          data-testid="sp-clear"
          className="px-3 py-1.5 text-[11px] uppercase font-mono tracking-wider border border-[#2A2A2A] text-[#A1A1AA] hover:text-white"
        >
          Clear
        </button>
        <div className="text-xs font-mono text-[#E1FF00]" data-testid="sp-count">
          {selectedIds.length} <span className="text-[#71717A]">of {senders.filter((s) => providerFilter === "all" || s.provider === providerFilter).length} selected</span>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <textarea
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          placeholder="Paste emails (comma, space, or newline separated) to bulk-select..."
          rows={2}
          data-testid="sp-bulk"
          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-1.5 text-[11px] text-white font-mono focus:border-[#E1FF00] focus:outline-none"
        />
        <div className="flex flex-col gap-1">
          <button
            onClick={bulkPaste}
            disabled={!bulk.trim()}
            data-testid="sp-bulk-apply"
            className={`px-3 py-1.5 text-[11px] uppercase font-mono tracking-wider ${!bulk.trim() ? "border border-[#2A2A2A] text-[#3A3A3A]" : "bg-[#E1FF00] text-black"}`}
          >
            Bulk select
          </button>
          <span data-testid="sp-bulk-result" className="text-[10px] text-[#71717A] font-mono"></span>
        </div>
      </div>

      <div className="border border-[#2A2A2A] max-h-96 overflow-y-auto">
        <table className="dtable w-full">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} data-testid="sp-thead-toggle" />
              </th>
              <th>Email</th><th>Provider</th><th>Proxy</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-[#71717A] py-6 font-mono">[ no senders match filter ]</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id}>
                <td>
                  <input type="checkbox" checked={selectedSet.has(s.id)}
                    onChange={(e) => {
                      const set = new Set(selectedIds);
                      if (e.target.checked) set.add(s.id); else set.delete(s.id);
                      onChange(Array.from(set));
                    }} />
                </td>
                <td className="font-mono text-xs">{s.email}</td>
                <td>{s.provider}</td>
                <td className="font-mono text-xs">{s.proxy_host || "—"}</td>
                <td><StatusBadge status={s.proxy_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ---- Test send panel (Review step) -----------------------------------
// Lets the operator send a real test email using the campaign's actual
// content + a chosen sender's proxy BEFORE going live. Reuses the
// existing /api/test-send endpoint so results (proxy, provider response,
// error) match what the campaign worker would produce.
function TestSendPanel({ form, senders }) {
  const [recipient, setRecipient] = useState("");
  const [senderId, setSenderId] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Only offer senders that are actually part of the campaign scope.
  const eligible = senders.filter((s) => {
    if (form.sender_ids && form.sender_ids.length > 0) return form.sender_ids.includes(s.id);
    if (form.sender_provider_filter === "all") return true;
    return s.provider === form.sender_provider_filter;
  });

  // Default the picker to the first eligible sender on mount.
  useEffect(() => {
    if (!senderId && eligible.length > 0) setSenderId(eligible[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible.length]);

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim());

  const doTest = async () => {
    if (!senderId || !valid) return;
    setSending(true); setResult(null);
    try {
      let contentMode = form.content_mode;
      let subject = form.inline_subject;
      let textBody = form.inline_text_body;
      let htmlBody = form.inline_html_body || "";

      // ai_pooled must use REAL pool content, not real-time DeepSeek. The
      // backend atomically CONSUMES (deletes) the oldest ready pool item —
      // exactly like a live campaign send — so the test verifies the actual
      // pre-generated content AND removes it from the pool just like a real
      // send. Nothing is generated on-the-fly and no DeepSeek tokens are spent.
      if (contentMode === "ai_realtime") {
        // Real-time AI campaigns still route through ai_studio for the test.
        contentMode = "ai_studio";
      }

      // Use the campaign's selected recipient list — the backend fetches
      // row 1 and injects real firstName/loanAmount/phone/etc. so the
      // test uses actual values, not empty placeholders.
      const listId = form.recipient_list_id || null;
      const payload = {
        sender_ids: [senderId],
        recipient_emails: [recipient.trim()],
        content_mode: contentMode,
        subject,
        text_body: textBody,
        html_body: htmlBody,
        sample_values: {},
        ai_instructions: form.ai_settings?.instructions || "",
        subject_min_chars: form.ai_settings?.subject_min_chars ?? 40,
        subject_max_chars: form.ai_settings?.subject_max_chars ?? 60,
        body_min_paragraphs: form.ai_settings?.body_min_paragraphs ?? 4,
        body_max_paragraphs: form.ai_settings?.body_max_paragraphs ?? 6,
        // Force thinking mode OFF for the pre-flight campaign test so we don't
        // burn the token budget on reasoning traces and end up with an empty
        // content field (repro: "AI generation failed: empty content field in
        // response — reasoning_content length=3245 chars…"). The operator can
        // still tune thinking in Content Pool / Test Send.
        ai_thinking: "disabled",
        preview_only: false,
        recipient_list_id: listId,
      };
      const r = await api.post("/test-send", payload);
      const item = r.data.results?.[0];
      setResult(item);
      if (item?.status === "provider_accepted") toast.success("Test email accepted by provider");
      else toast.error(`Test send: ${item?.status || "failed"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-4 mt-4" data-testid="cb-test-send">
      <div className="flex items-center gap-2 mb-3">
        <PaperPlaneTilt size={14} weight="bold" className="text-[#00E5FF]" />
        <div className="micro text-[#00E5FF]">Test Before Going Live</div>
      </div>
      <div className="text-[11px] text-[#71717A] font-mono mb-3 leading-relaxed">
        Send one real email through a selected mailbox (using its proxy) to verify content and delivery.
        {form.content_mode === "ai_pooled" && (
          <> Uses (and <span className="text-[#FF9500]">permanently consumes</span>) the{" "}
          <span className="text-[#00E5FF]">oldest ready item</span> from the Content Pool —
          the item is deleted after the test, exactly like a live send (no DeepSeek call).</>
        )}
        {form.recipient_list_id && (
          <> Placeholders (firstName, loanAmount, phone, ctaLink, …) are filled from{" "}
          <span className="text-[#00E5FF]">recipient row #1</span> of your selected list so the preview
          matches what the campaign will actually send.</>
        )}
        {" "}No campaign_recipients row is created; no live logs are affected.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="micro block mb-1">Sender mailbox</span>
          <select
            value={senderId}
            onChange={(e) => setSenderId(e.target.value)}
            data-testid="cb-test-sender"
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#00E5FF] focus:outline-none"
          >
            {eligible.length === 0 && <option value="">— no eligible senders —</option>}
            {eligible.map((s) => (
              <option key={s.id} value={s.id}>
                {s.email} · {s.provider} · {s.proxy_status || "no-proxy"}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="micro block mb-1">Test recipient email</span>
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="you@example.com"
            data-testid="cb-test-recipient"
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#00E5FF] focus:outline-none"
          />
        </label>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={doTest}
          disabled={sending || !valid || !senderId}
          data-testid="cb-test-send-btn"
          className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider ${
            sending || !valid || !senderId
              ? "border border-[#2A2A2A] text-[#3A3A3A]"
              : "bg-[#00E5FF] text-black hover:bg-[#00C4DA]"
          }`}
        >
          <PaperPlaneTilt size={14} weight="bold" />
          {sending ? "Sending..." : "Send test email"}
        </button>
        {!valid && recipient && (
          <span className="text-[10px] text-[#FF9500] font-mono">invalid email</span>
        )}
      </div>

      {result && (
        <div className="mt-4 border border-[#2A2A2A] p-3 space-y-2" data-testid="cb-test-result">
          <div className="flex items-center gap-2">
            <StatusBadge status={result.status} />
            <span className="font-mono text-[11px] text-[#A1A1AA]">
              {result.sender_email} → {result.recipient_email}
            </span>
          </div>
          <div>
            <div className="micro mb-1">Subject</div>
            <div className="font-mono text-xs text-white bg-[#0F0F0F] border border-[#2A2A2A] p-2 break-words">
              {result.subject || "—"}
            </div>
          </div>
          {result.generated_body && (
            <div>
              <div className="micro mb-1">Body</div>
              <pre className="whitespace-pre-wrap font-mono text-[11px] text-[#E4E4E7] bg-[#0F0F0F] border border-[#2A2A2A] p-2 max-h-40 overflow-y-auto">
                {result.generated_body}
              </pre>
            </div>
          )}
          {result.provider_response && (
            <div className="text-[11px] font-mono text-[#71717A]">
              <span className="text-[#00E5FF]">provider:</span> {result.provider_response}
            </div>
          )}
          {(result.error_message || result.proxy_error_message) && (
            <div className="text-[11px] font-mono text-[#FF3B30]">
              {result.error_message || result.proxy_error_message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
